import { Alert, App, Button } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../../../constants/routes.ts"

import { useAppDispatch } from "../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../hooks/use-app-selector.ts"
import { useNotifications } from "../../../../../hooks/use-notifications.ts"
import { errorSave } from "../../../../../redux/errors.slice.ts"
import { unwrapEnvelope } from "../../../../../utils/api/unwrap-envelope.ts"
import {
  firstFieldError,
  getResponseMessage
} from "../../../../../utils/handlers/get-response-message.ts"
import { unknownError } from "../../../../../utils/handlers/handle-error.tsx"
import { PROJECT_MANAGER_REQUIRED } from "../../../../Leads/helpers/project-manager-required.ts"
import { useProjectManagerPrompt } from "../../../../Leads/hooks/use-project-manager-prompt.tsx"
import { getProject } from "../../../../Project/api/project.services.ts"
import type { Project } from "../../../../Project/types/project.types.ts"
import {
  approveLead,
  getProjectsFilterOptions
} from "../../../../Projects/api/services.ts"
import { fetchLead } from "../../../redux/lead.thunks.ts"
import type { Lead } from "../../../types/lead.types.ts"

const W = "statusAutomation.waitingForProjectManager"

interface Props {
  lead: Lead
  /** `lead_edit`: only someone who can edit the lead is asked to act on it. */
  canEdit: boolean
}

/**
 * A Won lead the Won → Project rule could not convert because it has no
 * Project Manager. The server decides that (`awaiting_project_manager`); a
 * payload without the field is read as not waiting.
 */
export function LeadWaitingForProjectManager({ lead, canEdit }: Props) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const notification = useNotifications()
  const currentUserId = useAppSelector((state) => state.auth?.userInfo?.id)
  // The only write that gives an unconverted lead its PM is the Approve that
  // converts it, which answers to this right rather than `lead_edit`.
  const canAssign = !!useAppSelector(
    (state) =>
      state?.auth?.userInfo?.permissions?.codes?.projects?.features
        ?.project_approval
  )
  const [loadingManagers, setLoadingManagers] = useState(false)

  const prompt = useProjectManagerPrompt<string>({
    submit: async (leadId, projectManagerId) => {
      const response = await approveLead(leadId, projectManagerId)
      if (!response?.ok) {
        // A refusal names a field (`project_type`) or carries a `detail`.
        dispatch(
          errorSave(
            getResponseMessage(response?.data) ||
              firstFieldError(response?.data) ||
              unknownError
          )
        )
        throw response?.data ?? null
      }
      void announceProjectCreated(leadId)
      // The lead comes back a project, which retires this notice.
      void dispatch(fetchLead(leadId))
    },
    defaultProjectManagerId: currentUserId
  })

  // The project keeps the lead's id; its number is the project's own.
  async function announceProjectCreated(projectId: string) {
    const response = await getProject(projectId)
    const project =
      response?.ok && response.data
        ? unwrapEnvelope<Project | null>(response.data)
        : null
    // `uid` is the number people read ("P-000009"); `project_number` is its bare sequence.
    const number = project?.uid || project?.project_number || lead.uid || ""
    notification.success({
      message: t(`${W}.projectCreated`, { number }),
      actions: (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            navigate(RoutesPaths.project.view(projectId).overview)
            notification.destroy()
          }}
        >
          {t(`${W}.openProject`)}
        </Button>
      )
    })
  }

  if (!canEdit || lead.awaiting_project_manager !== true) return prompt.dialog

  const openPicker = async () => {
    setLoadingManagers(true)
    try {
      const response = await getProjectsFilterOptions()
      const options =
        response?.ok && response.data
          ? unwrapEnvelope<{ project_manager?: BaseOptionType[] } | null>(
              response.data
            )
          : null
      if (!Array.isArray(options?.project_manager)) {
        message.error(t(`${W}.loadFailed`))
        return
      }
      prompt.ask(
        {
          type: PROJECT_MANAGER_REQUIRED,
          projectManagers: options.project_manager
        },
        lead.id
      )
    } finally {
      setLoadingManagers(false)
    }
  }

  return (
    <>
      {prompt.dialog}
      <Alert
        type="warning"
        showIcon
        className="mt-3"
        data-testid="lead-waiting-for-project-manager"
        message={t(`${W}.message`)}
        description={t(canAssign ? `${W}.description` : `${W}.needsApprover`)}
        action={
          canAssign ? (
            <Button
              size="small"
              type="primary"
              loading={loadingManagers}
              onClick={() => void openPicker()}
            >
              {t(`${W}.assign`)}
            </Button>
          ) : undefined
        }
      />
    </>
  )
}
